if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.galleryMode = this.dataset.galleryMode || 'legacy';
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');

        if (this.galleryMode === 'embla') {
          this.initEmblaGallery();
          return;
        }

        this.enableLoopNavigation = this.elements.viewer?.dataset.loop === 'true';
        this.touchStartX = null;
        this.touchStartY = null;
        this.swipeThreshold = 30;

        if (this.enableLoopNavigation && this.elements.viewer) {
          this.elements.viewer.addEventListener('click', this.onViewerNavigationClick.bind(this), true);
          if (this.elements.viewer.slider) {
            this.elements.viewer.slider.addEventListener('touchstart', this.onViewerTouchStart.bind(this), {
              passive: true,
            });
            this.elements.viewer.slider.addEventListener('touchend', this.onViewerTouchEnd.bind(this), {
              passive: true,
            });
          }
          this.bindLoopButtonObserver();
          this.syncLoopButtons();
          requestAnimationFrame(() => this.syncLoopButtons());
          window.setTimeout(() => this.syncLoopButtons(), 120);
        }

        this.elements.viewer?.addEventListener('slideChanged', this.onSlideChanged.bind(this));
        if (!this.elements.thumbnails) return;

        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          const button = mediaToSwitch.querySelector('button');
          if (!button) return;
          button.addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      disconnectedCallback() {
        if (this.loopButtonObserver) this.loopButtonObserver.disconnect();
        if (this.emblaMain) this.emblaMain.destroy();
        if (this.emblaThumbs) this.emblaThumbs.destroy();
      }

      initEmblaGallery() {
        if (typeof window.EmblaCarousel === 'undefined' || !this.elements.viewer) return;

        this.emblaViewport = this.querySelector('.embla-product-gallery__viewport');
        if (!this.emblaViewport) return;

        this.emblaMain = window.EmblaCarousel(this.emblaViewport, {
          align: 'start',
          containScroll: 'trimSnaps',
          dragFree: false,
          loop: true,
        });

        this.bindEmblaPointerSuppression();
        this.bindEmblaNavigationButtons();
        this.bindEmblaThumbnails();
        this.emblaMain.on('init', this.onEmblaSelect.bind(this));
        this.emblaMain.on('reInit', this.onEmblaSelect.bind(this));
        this.emblaMain.on('select', this.onEmblaSelect.bind(this));
        this.onEmblaSelect();
      }

      bindEmblaPointerSuppression() {
        this.pointerStart = null;
        this.suppressClickUntil = 0;

        this.emblaViewport.addEventListener('pointerdown', (event) => {
          this.pointerStart = {
            x: event.clientX,
            y: event.clientY,
          };
        });

        this.emblaViewport.addEventListener('pointermove', (event) => {
          if (!this.pointerStart) return;

          const deltaX = Math.abs(event.clientX - this.pointerStart.x);
          const deltaY = Math.abs(event.clientY - this.pointerStart.y);
          if (deltaX > 6 || deltaY > 6) {
            this.suppressClickUntil = Date.now() + 250;
          }
        });

        const clearPointerState = () => {
          this.pointerStart = null;
        };

        this.emblaViewport.addEventListener('pointerup', clearPointerState);
        this.emblaViewport.addEventListener('pointercancel', clearPointerState);
        this.emblaViewport.addEventListener('pointerleave', clearPointerState);

        this.emblaViewport.addEventListener(
          'click',
          (event) => {
            if (Date.now() <= this.suppressClickUntil) {
              event.preventDefault();
              event.stopPropagation();
              event.stopImmediatePropagation();
            }
          },
          true
        );
      }

      bindEmblaNavigationButtons() {
        this.prevButton = this.querySelector('.slider-button[name="previous"]');
        this.nextButton = this.querySelector('.slider-button[name="next"]');
        this.currentCounter = this.querySelector('.slider-counter--current');
        this.totalCounter = this.querySelector('.slider-counter--total');

        if (this.totalCounter && this.emblaMain) {
          this.totalCounter.textContent = String(this.emblaMain.scrollSnapList().length);
        }

        if (this.prevButton) {
          this.prevButton.addEventListener('click', (event) => {
            event.preventDefault();
            this.emblaMain?.scrollPrev();
          });
        }

        if (this.nextButton) {
          this.nextButton.addEventListener('click', (event) => {
            event.preventDefault();
            this.emblaMain?.scrollNext();
          });
        }
      }

      bindEmblaThumbnails() {
        if (!this.elements.thumbnails) return;

        this.thumbnailItems = Array.from(this.elements.thumbnails.querySelectorAll('[data-target]'));
        this.thumbnailItems.forEach((mediaToSwitch, index) => {
          const button = mediaToSwitch.querySelector('button');
          if (!button) return;
          button.addEventListener('click', (event) => {
            event.preventDefault();
            this.emblaMain?.scrollTo(index);
          });
        });
      }

      onEmblaSelect() {
        if (!this.emblaMain) return;

        const selectedIndex = this.emblaMain.selectedScrollSnap();
        const slideItems = Array.from(this.querySelectorAll('.embla-product-gallery__slide[data-media-id]'));

        slideItems.forEach((item, index) => {
          item.classList.toggle('is-active', index === selectedIndex);
        });

        if (this.currentCounter) {
          this.currentCounter.textContent = String(selectedIndex + 1);
        }

        if (!this.thumbnailItems) return;

        this.thumbnailItems.forEach((item, index) => {
          const button = item.querySelector('button');
          if (!button) return;
          if (index === selectedIndex) {
            button.setAttribute('aria-current', 'true');
          } else {
            button.removeAttribute('aria-current');
          }
        });

        const activeThumbnail = this.thumbnailItems[selectedIndex];
        if (activeThumbnail) this.scrollActiveThumbnailIntoView(activeThumbnail);

        const activeSlide = slideItems[selectedIndex];
        const position = this.thumbnailItems[selectedIndex]?.dataset.mediaPosition || String(selectedIndex + 1);
        if (activeSlide) this.announceLiveRegion(activeSlide, position);
      }

      onSlideChanged(event) {
        if (this.elements.thumbnails) {
          const activeMediaId =
            this.elements.viewer?.querySelector('.product__media-item.is-active[data-media-id]')?.dataset?.mediaId ||
            event?.detail?.currentElement?.dataset?.mediaId;
          if (!activeMediaId) return;
          const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${activeMediaId}"]`);
          this.setActiveThumbnail(thumbnail);
        }
        this.syncLoopButtons();
      }

      onViewerNavigationClick(event) {
        if (!this.enableLoopNavigation || !this.elements.viewer) return;

        const button = event.target.closest('button[name="previous"], button[name="next"]');
        if (!button) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        this.goToRelativeMedia(button.name === 'next' ? 1 : -1);
      }

      onViewerTouchStart(event) {
        if (!this.enableLoopNavigation || !event.changedTouches?.length) return;
        this.touchStartX = event.changedTouches[0].clientX;
        this.touchStartY = event.changedTouches[0].clientY;
      }

      onViewerTouchEnd(event) {
        if (
          !this.enableLoopNavigation ||
          this.touchStartX === null ||
          this.touchStartY === null ||
          !event.changedTouches?.length
        ) {
          return;
        }

        const touch = event.changedTouches[0];
        const deltaX = touch.clientX - this.touchStartX;
        const deltaY = touch.clientY - this.touchStartY;

        this.touchStartX = null;
        this.touchStartY = null;

        if (Math.abs(deltaX) < this.swipeThreshold || Math.abs(deltaX) <= Math.abs(deltaY)) return;
        this.goToRelativeMedia(deltaX < 0 ? 1 : -1);
      }

      goToRelativeMedia(direction) {
        if (!this.elements.viewer) return;
        const mediaItems = Array.from(this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]'));
        if (mediaItems.length < 2) return;

        const currentIndex = mediaItems.findIndex((item) => item.classList.contains('is-active'));
        const safeCurrentIndex = currentIndex >= 0 ? currentIndex : 0;
        const targetIndex = (safeCurrentIndex + direction + mediaItems.length) % mediaItems.length;
        const targetMediaId = mediaItems[targetIndex].dataset.mediaId;
        this.setActiveMedia(targetMediaId, false);
      }

      setActiveMedia(mediaId, prepend) {
        if (this.galleryMode === 'embla') {
          if (!this.emblaMain) return;
          const slideItems = Array.from(this.querySelectorAll('.embla-product-gallery__slide[data-media-id]'));
          const targetIndex = slideItems.findIndex((item) => item.dataset.mediaId === mediaId);
          if (targetIndex === -1) return;
          this.emblaMain.scrollTo(targetIndex);
          this.onEmblaSelect();
          return;
        }

        const activeMediaItems = Array.from(
          this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]')
        );
        const activeMedia =
          this.elements.viewer.querySelector(`.product__media-item[data-media-id="${mediaId}"]`) || activeMediaItems[0];
        if (!activeMedia) {
          return;
        }
        activeMediaItems.forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail && activeThumbnail.parentElement.firstChild !== activeThumbnail) {
              activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);
        this.syncLoopButtons();

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (!activeThumbnail) return;
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
        const thumbnailButton = thumbnail.querySelector('button');
        if (!thumbnailButton) return;
        thumbnailButton.setAttribute('aria-current', true);
        this.scrollActiveThumbnailIntoView(thumbnail);
      }

      scrollActiveThumbnailIntoView(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;
        if (typeof this.elements.thumbnails.isSlideVisible !== 'function' || !this.elements.thumbnails.slider) return;
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img, .product__media img');
        if (!image || !this.elements.liveRegion) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        if (this.galleryMode === 'embla') return;
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }

      syncLoopButtons() {
        if (!this.enableLoopNavigation || !this.elements.viewer) return;
        const prevButton = this.elements.viewer.querySelector('button[name="previous"]');
        const nextButton = this.elements.viewer.querySelector('button[name="next"]');
        if (prevButton) prevButton.removeAttribute('disabled');
        if (nextButton) nextButton.removeAttribute('disabled');
      }

      bindLoopButtonObserver() {
        if (!this.enableLoopNavigation || !this.elements.viewer || this.loopButtonObserver) return;

        const prevButton = this.elements.viewer.querySelector('button[name="previous"]');
        const nextButton = this.elements.viewer.querySelector('button[name="next"]');
        if (!prevButton && !nextButton) return;

        this.loopButtonObserver = new MutationObserver(() => this.syncLoopButtons());
        if (prevButton) this.loopButtonObserver.observe(prevButton, { attributes: true, attributeFilter: ['disabled'] });
        if (nextButton) this.loopButtonObserver.observe(nextButton, { attributes: true, attributeFilter: ['disabled'] });
      }
    }
  );
}
